/**
 * 会员中心：展示当前会员流量/用量统计
 */

import { useEffect, useState } from 'react'
import { getMe, getMemberUsage, logout, UsageSummary } from '../api'

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${m}m ${s}s`
}

export default function MemberDashboard() {
  const [user, setUser] = useState<UsageSummary['user'] | null>(null)
  const [usage, setUsage] = useState<UsageSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadData = async () => {
    try {
      const meRes = await getMe()
      setUser(meRes.user)
      const usageRes = await getMemberUsage()
      setUsage(usageRes)
    } catch (err) {
      const msg = err instanceof Error ? err.message : '加载失败'
      if (msg.includes('登录') || msg.includes('401')) {
        window.location.href = '/login'
        return
      }
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const handleLogout = async () => {
    try {
      await logout()
    } catch { }
    window.location.href = '/'
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-50 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        {/* 顶部导航 */}
        {/* 🌟 优化：将 flex 调整为移动端下自动换行 (flex-wrap) 或上下排列，防止内容拥挤 */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">会员中心</h1>
            <p className="text-slate-500 text-sm mt-1">查看你的应用使用情况和流量统计</p>
          </div>
          {/* 🌟 优化：缩小了移动端下的按钮间距 (gap-2) */}
          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href="/"
              // 🌟 修复：去掉了 hidden sm:inline-flex，并针对移动端调小了字号 (text-xs) 和内边距 (px-3)
              className="inline-flex px-3 py-2 sm:px-4 sm:py-2 rounded-xl bg-white border border-slate-200 text-slate-600 text-xs sm:text-sm font-semibold hover:bg-slate-50 transition-colors whitespace-nowrap"
            >
              返回首页
            </a>
            <button
              onClick={handleLogout}
              // 🌟 优化：同样为登出按钮增加了移动端的紧凑适配，并添加 whitespace-nowrap 防止文字被挤换行
              className="px-3 py-2 sm:px-4 sm:py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap"
            >
              退出登录
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-500 text-sm">加载中...</div>
        ) : error ? (
          <div className="rounded-2xl bg-rose-50 border border-rose-200 px-6 py-5 text-rose-700 text-sm">
            {error}
          </div>
        ) : (
          <>
            {/* 账户信息卡 */}
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl border border-white/60 shadow-[0_12px_40px_-12px_rgba(100,116,139,0.15)] p-6 mb-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-500 text-white text-2xl flex items-center justify-center shadow-lg shadow-sky-300/40">
                  👤
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-800">{user?.username || user?.email}</h2>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                  <span className="inline-block mt-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-100">
                    已验证会员
                  </span>
                </div>
              </div>
            </div>

            {/* 用量统计 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard
                label="聊天回合"
                value={String(usage?.chat_turns || 0)}
                icon="💬"
                gradient="from-sky-500 to-indigo-500"
              />
              <StatCard
                label="同传时长"
                value={formatDuration(usage?.usage_by_feature?.translate?.duration_seconds || 0)}
                icon="🌐"
                gradient="from-cyan-500 to-sky-500"
              />
              <StatCard
                label="上行流量"
                value={formatBytes(
                  Object.values(usage?.usage_by_feature || {}).reduce((sum, u) => sum + (u.input_bytes || 0), 0)
                )}
                icon="⬆️"
                gradient="from-indigo-500 to-cyan-500"
              />
              <StatCard
                label="下行流量"
                value={formatBytes(
                  Object.values(usage?.usage_by_feature || {}).reduce((sum, u) => sum + (u.output_bytes || 0), 0)
                )}
                icon="⬇️"
                gradient="from-sky-400 to-cyan-400"
              />
            </div>

            {/* 功能明细 */}
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl border border-white/60 shadow-[0_12px_40px_-12px_rgba(100,116,139,0.15)] p-6">
              <h3 className="text-lg font-bold text-slate-800 mb-4">功能使用明细</h3>
              {Object.keys(usage?.usage_by_feature || {}).length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">暂无使用记录，快去体验聊天伙伴、同声传译或双向同传吧～</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-100">
                        <th className="pb-3 font-medium">功能</th>
                        <th className="pb-3 font-medium">次数</th>
                        <th className="pb-3 font-medium">时长</th>
                        <th className="pb-3 font-medium">上行</th>
                        <th className="pb-3 font-medium">下行</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-700">
                      {Object.entries(usage?.usage_by_feature || {}).map(([feature, data]) => (
                        <tr key={feature} className="border-b border-slate-50 last:border-0">
                          <td className="py-3 font-semibold capitalize">{feature}</td>
                          <td className="py-3">{data.sessions}</td>
                          <td className="py-3">{formatDuration(data.duration_seconds)}</td>
                          <td className="py-3">{formatBytes(data.input_bytes)}</td>
                          <td className="py-3">{formatBytes(data.output_bytes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function StatCard({
  label,
  value,
  icon,
  gradient,
}: {
  label: string
  value: string
  icon: string
  gradient: string
}) {
  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-white/60 shadow-[0_8px_30px_-10px_rgba(100,116,139,0.12)] p-5 flex items-start gap-4">
      <div
        className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} text-white text-xl flex items-center justify-center shadow-md`}
      >
        {icon}
      </div>
      <div>
        <div className="text-2xl font-bold text-slate-800">{value}</div>
        <div className="text-xs text-slate-500 font-medium">{label}</div>
      </div>
    </div>
  )
}
